"use client";

import { useTranslations } from "next-intl";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

import { WidgetOpenLink } from "./widget-open-link";

import type { WidgetDefinition, WidgetResult } from "@/types";

/**
 * The surface every widget card wears: DS fill, radius and lift, plus a lit edge
 * that exists only on dark.
 *
 * In light the DS card is a flat surface whose lift comes entirely from
 * --shadow-widget, and a white hairline there is invisible. On dark the card and
 * the page sit close enough in value that without an edge the card dissolves into
 * the background — so dark adds a 12% hairline, a deeper drop shadow, and 2px of
 * inner glow along the border. 2px, not the 10px of --shadow-mint-glow: that token
 * was drawn for light, where a wide inner wash reads as sheen; on dark it reads as
 * fog over the content.
 */
const WIDGET_SURFACE_CLASS =
  "relative overflow-hidden rounded-card bg-widget-surface shadow-widget dark:border dark:border-white/12 dark:shadow-[10px_14px_34px_0_rgba(0,0,0,0.35),inset_0_0_2px_0_rgba(255,255,255,0.22)]";

/**
 * The mint under the glass — one soft pool of light centred in the card.
 *
 * Sized as a SHARE of the card (52% x 30%) rather than a fixed disc, and faded by
 * `blur` rather than by a gradient stop. Both matter for the same reason: a widget
 * card is anything from a short wide header tile to a tall grid card, and a
 * fixed-size shape that sits nicely in one bleeds off the edges of the other —
 * which is exactly what a 130px disc did to the header strip. A percentage shape
 * keeps the same margin on every side at every size, and a blur trails off where a
 * gradient stop visibly cuts.
 *
 * --foreground-mint, NOT --brand-mint: brand mint is the light theme's pale
 * surface tint (#c4e4e1) and dark mode redefines it to a grey (rgb(52,55,62)), so
 * a glow drawn with it simply vanishes on dark. --foreground-mint is the accent
 * that stays mint in both themes (teal-600 light, teal-400 dark). Dark carries
 * more of it, because the same glow that is plenty against a near-white card
 * barely registers against a dark one.
 */
function WidgetGlow() {
  return (
    <span
      aria-hidden
      className="pointer-events-none absolute top-1/2 left-1/2 h-[30%] w-[52%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-foreground-mint opacity-[0.08] blur-[40px] dark:opacity-[0.12]"
    />
  );
}

/**
 * Card chrome shared by every widget: title, optional "view all" link, and the
 * loading / failed states.
 *
 * The card owns loading and error; the RENDERER owns "empty", because only the
 * widget's own payload knows whether empty means "nothing yet" or something more
 * specific (e.g. "chat isn't set up for your account").
 */
export function WidgetCard({
  definition,
  result,
  isLoading,
  onRetry,
  viewAllHref,
  action,
  compact = false,
  children,
}: {
  definition: WidgetDefinition;
  result: WidgetResult | undefined;
  isLoading: boolean;
  onRetry: () => void;
  viewAllHref?: string | null;
  /**
   * The corner's one control (settings, an open action or help) in place of the ↗ link; set it
   * only on cards without a viewAllHref, since two chips in 32 pixels would need a real menu.
   */
  action?: React.ReactNode;
  /**
   * Header-strip tiles: tighter padding and no title row, because a count tile
   * already renders its own label and the widget title would just repeat it.
   */
  compact?: boolean;
  children: React.ReactNode;
}) {
  const t = useTranslations("Dashboard");

  // Header tiles are real shadcn Cards (the dashboard-01 count-card shape): the
  // Card supplies the vertical rhythm and CardHeader/CardAction the
  // label / number / ↗-chip grid. StatWidget fills those slots — including its
  // own WidgetOpenLink, since the href belongs to the figure rather than to the
  // widget (one payload can render several tiles, each pointing somewhere else).
  if (compact) {
    return (
      // The same surface and glow as the grid cards, so the strip and the grid
      // read as one system. No title row: a count tile's own label is its title,
      // and a header on top of that would say the same word twice.
      //
      // 18px padding rather than the shadcn size=sm 16px, and the same figure on
      // every side — the reference tile's own metric, and what gives the big
      // number room to sit off the edge.
      <Card
        size="sm"
        className={cn(
          WIDGET_SURFACE_CLASS,
          "h-full border-0 py-[18px] ring-0 [&_[data-slot=card-footer]]:px-[18px] [&_[data-slot=card-header]]:px-[18px]",
        )}
      >
        <WidgetGlow />
        {isLoading ? (
          // Mirrors the tile's own shape (small label over a big number) rather
          // than the feed-row skeleton, so it doesn't flash an avatar circle.
          <CardContent className="flex flex-col gap-2">
            <Skeleton className="h-3 w-20" />
            {/* Tracks the figure's own size (32px, 38px on lg) so the tile
                doesn't resize when the number lands. */}
            <Skeleton className="h-8 w-28 lg:h-[38px]" />
          </CardContent>
        ) : result?.status === "error" ? (
          <CardContent className="flex items-center justify-between gap-2">
            <p className="text-sm text-fg-muted">{t("loadFailed")}</p>
            <Button variant="outline" size="sm" onClick={onRetry}>
              {t("retry")}
            </Button>
          </CardContent>
        ) : (
          children
        )}
      </Card>
    );
  }

  return (
    // The DS card: one flat surface, no border and no banded header — the lift
    // comes entirely from the wide soft shadow, and the title simply sits at the
    // top of the body. The band this replaced was the app's own invention; the
    // design system draws no rule between a card's title and its contents.
    //
    // 15px padding on every side (--card-padding / --sp-4), which the DS uses as
    // both the screen margin and the card padding.
    //
    // h-full, not auto height: grid rows are as tall as their tallest card, so a
    // short card next to a long one leaves a dead gap under it before the next
    // row starts. Filling the row moves that space inside the card, where it
    // reads as padding instead of a hole in the layout.
    <div className={cn(WIDGET_SURFACE_CLASS, "flex h-full flex-col p-[15px]")}>
      <WidgetGlow />
      {/* Title left, the DS's signature ↗ chip right. 14px regular, not the
          15px semibold the band used: in the DS a card title is quiet body text,
          and the number below it is what carries weight. */}
      <div className="relative flex items-start justify-between gap-2">
        <h3 className="truncate font-display text-sm leading-tight text-foreground">
          {definition.title}
        </h3>
        {action ?? <WidgetOpenLink href={viewAllHref} />}
      </div>

      <div className="relative flex flex-1 flex-col pt-3">
        {isLoading ? (
          <WidgetSkeleton />
        ) : result?.status === "error" ? (
          // One widget throwing is contained to its own key server-side, so the
          // rest of the dashboard is fine — this card just offers a retry.
          <div className="flex flex-1 flex-col items-center justify-center gap-3 py-10 text-center">
            <p className="font-display text-sm text-fg-muted">
              {t("loadFailed")}
            </p>
            <Button variant="outline" size="sm" onClick={onRetry}>
              {t("retry")}
            </Button>
          </div>
        ) : (
          children
        )}
      </div>
    </div>
  );
}

export function WidgetSkeleton({ rows = 4 }: { rows?: number }) {
  return (
    <div className="flex flex-col gap-3">
      {Array.from({ length: rows }).map((_, index) => (
        <div key={index} className="flex items-center gap-3">
          <Skeleton className="h-9 w-9 shrink-0 rounded-full" />
          <div className="flex flex-1 flex-col gap-1.5">
            <Skeleton className="h-3.5 w-1/2" />
            <Skeleton className="h-3 w-3/4" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function WidgetEmpty({
  label,
  className,
}: {
  label: string;
  className?: string;
}) {
  return (
    <div
      className={cn(
        // flex-1 so the label centres in whatever height the row gives the card,
        // rather than sitting at the top of a stretched one.
        "flex min-h-[120px] flex-1 items-center justify-center font-display text-sm text-fg-muted",
        className,
      )}
    >
      {label}
    </div>
  );
}
